import React, { useCallback } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import db from '../../lib/db';
import { sentenceCase } from '../../lib/utils';
import { OPEN_WIDGET_BUILDER_EVENT, recordWidgetGenerationActivityDismissal } from '../../lib/widgetGenerationNotifications';

import SettingsXIcon from '../../../assets/images/settings-x.svg';

import { useWidgetGenerationBanners } from '../../hooks/useWidgetGenerationBanners';
import WidgetAttentionIndicator from './WidgetAttentionIndicator';
import WidgetIcon from './WidgetIcon';

const WidgetGenerationBanner = React.memo(function WidgetGenerationBanner({ banner }) {
  const cachedResources = useLiveQuery(
    async function loadBannerResources() {
      const widgetQuery = banner.widgetId
        ? db.widgets.get([banner.workspaceId, banner.widgetId])
        : db.widgets.where('[workspace_id+agent_chat_id]').equals([banner.workspaceId, banner.agentChatId]).first();
      const [agentChat, widget] = await Promise.all([db.agent_chats.get([banner.workspaceId, banner.agentChatId]), widgetQuery]);

      return { agentChat, widget };
    },
    [banner.agentChatId, banner.widgetId, banner.workspaceId],
  );
  const name = sentenceCase(cachedResources?.widget?.title || cachedResources?.agentChat?.title || banner.name || 'New widget');
  const handleOpen = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent(OPEN_WIDGET_BUILDER_EVENT, {
        detail: {
          workspaceId: banner.workspaceId,
          widgetId: banner.widgetId,
          agentChatId: banner.agentChatId,
          runId: banner.runId,
        },
      }),
    );
  }, [banner]);
  const handleDismiss = useCallback(
    (event) => {
      event.stopPropagation();
      recordWidgetGenerationActivityDismissal(banner.cycleId, {
        workspaceId: banner.workspaceId,
        agentChatId: banner.agentChatId,
      });
    },
    [banner],
  );

  return (
    <div className="group/banner flex w-full items-center gap-2 rounded-lg border-[0.5px] border-white bg-gray-50 px-3 py-2 shadow-[0_9px_32px_rgba(0,0,0,0.08)]">
      <button
        type="button"
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
        onClick={handleOpen}
        aria-label={`${banner.label}: ${name}`}
      >
        <WidgetAttentionIndicator state={banner.kind} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-sans text-[12px]/[16px] font-medium text-black">{name}</span>
          <span className="truncate font-sans text-[11px]/[14px] text-gray-600">{banner.label}</span>
        </span>
      </button>
      <span className="inline-flex size-8 shrink-0">
        <WidgetIcon widget={cachedResources?.widget} className="size-8" />
      </span>
      <button
        type="button"
        className="flex size-4 shrink-0 cursor-pointer items-center justify-center text-gray-700 opacity-0 transition-opacity duration-100 group-hover/banner:opacity-100 hover:text-black focus-visible:opacity-100"
        onClick={handleDismiss}
        aria-label={`Dismiss ${name} notification`}
      >
        <SettingsXIcon className="size-4" />
      </button>
    </div>
  );
});

export default function WidgetGenerationBannerList({ workspaceId }) {
  const banners = useWidgetGenerationBanners(workspaceId);
  const renderBanner = useCallback(
    (banner) => <WidgetGenerationBanner key={`${banner.workspaceId}:${banner.agentChatId}`} banner={banner} />,
    [],
  );

  if (banners.length === 0) return null;

  return <div className="flex w-full flex-col gap-1">{banners.map(renderBanner)}</div>;
}
